import { useLatest } from 'ahooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor } from '@meitu/saas-editor-core';
import { CollapseType } from '@/types';

type IUsePressToDeleteProps = {
    editor: Editor;
    handleAfterCopyOrDel: () => void;
    isPickStage: boolean;
    isFocusInNavigation: boolean;
    cb?: () => void;
};
// 添加删除页面的快捷键处理
export const usePressToDelete = ({
    editor,
    handleAfterCopyOrDel,
    isPickStage,
    isFocusInNavigation,
    cb,
}: IUsePressToDeleteProps) => {
    const [selectedIndexes, setSelectedIndexes] = useState<number[]>(editor.stage.getStageSelection());
    useEffect(() => {
        return editor.stage.onStageSelectionChange((indexes) => {
            setSelectedIndexes(indexes);
        });
    }, []);

    const selectedIndexesRef = useLatest(selectedIndexes);
    const isPickStageRef = useLatest(isPickStage);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Delete' || e.key === 'Backspace') {
                const selectedIndexes = selectedIndexesRef.current || [];
                if (
                    !isPickStageRef.current ||
                    selectedIndexes.length < 1 ||
                    (e.target as HTMLElement)?.tagName === 'INPUT'
                ) {
                    return;
                }

                // 焦点不在导航栏, 不响应删除事件
                if (!isFocusInNavigation) {
                    return;
                }

                e.preventDefault();
                cb?.();
                editor.stage.batchDelete(selectedIndexes);
                handleAfterCopyOrDel();
            }
        },
        [isFocusInNavigation, selectedIndexesRef.current, isPickStageRef.current],
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isFocusInNavigation, selectedIndexesRef.current, isPickStageRef.current]);
};

type IMultiSelectState = Set<number>;

// 用于多选功能
export const useMultiSelectState = (editor: Editor) => {
    const multiSelectState = useRef<IMultiSelectState>(new Set());

    const handleMultiSelect = useCallback((index: number, event: React.MouseEvent) => {
        const activeIndex = editor.active.getActiveStageIndex();
        const state = multiSelectState.current;

        // Command/Ctrl + 点击实现非连续选择
        if (event.metaKey || event.ctrlKey) {
            if (index === activeIndex) return;
            if (state.has(index)) {
                state.delete(index);
            } else {
                state.add(index);
            }
        }
        // Shift + 点击实现连续选择
        else if (event.shiftKey) {
            if (state.has(index)) return;
            state.add(index);

            const selectedIndexes = Array.from(state);
            const start = Math.min(...selectedIndexes);
            const end = Math.max(...selectedIndexes);

            state.clear();
            for (let i = start; i <= end; i++) {
                state.add(i);
            }
        }
        // 普通点击
        else {
            state.clear();
            state.add(index);
            // 普通点击时, 激活舞台为点击的舞台，多选时不改变当前激活舞台
            editor.stage.setActiveStageIndex(index);
            editor.stage.scrollToActiveStage();
        }

        editor.stage.setStageSelection(Array.from(state).sort((a, b) => a - b));
    }, []);

    const clearSelection = useCallback(() => {
        multiSelectState.current.clear();
        editor.stage.setStageSelection([]);
    }, []);

    const setSelection = useCallback((selectedIndexes: number[]) => {
        multiSelectState.current.clear();
        if (selectedIndexes.length) {
            selectedIndexes.forEach((index) => multiSelectState.current.add(index));
        }
        editor.stage.setStageSelection(Array.from(multiSelectState.current).sort((a, b) => a - b));
    }, []);

    // 用于设置新页面选中
    const setNewPageSelected = useCallback((newIndex: number | number[]) => {
        const state = multiSelectState.current;
        const indexes = Array.isArray(newIndex) ? newIndex : [newIndex];
        state.clear();
        indexes.forEach((index) => state.add(index));
        editor.stage.setStageSelection([...state].sort((a, b) => a - b));
    }, []);

    // 处理复制或删除后的选中逻辑
    const handleAfterCopyOrDel = useCallback((isPaste: boolean = false) => {
        setTimeout(() => {
            const activeIndex = editor.active.getActiveStageIndex();
            if (isPaste) {
                const stageSelection = editor.stage.getStageSelection();
                const addIndexes = Array.from({ length: stageSelection.length }, (_, i) => activeIndex + i);
                setNewPageSelected(addIndexes);
            } else {
                setNewPageSelected(activeIndex);
            }
            editor.stage.scrollToActiveStage();
        }, 0);
    }, []);

    return {
        handleMultiSelect,
        handleAfterCopyOrDel,
        setSelection,
        clearSelection,
    };
};
